import React from 'react';
import classNames from 'classnames';

import Locale from 'data/locale';
import { getProfilePath } from 'common';

const _L = Locale("ui-import");

function SearchNav({ results, onSearch, disabled }) {
  const start = results.start || 0;
  const length = results.results.length;
  const count = results.count || length;
  const step = results.step || 10;

  const pages = Math.floor((count + step - 1) / step);

  const onHome = React.useCallback(e => {
    if (!disabled && start > 0) onSearch({start: 0});
  }, [disabled, start, onSearch]);
  const onPrev = React.useCallback(e => {
    if (!disabled && start > 0) onSearch({start: Math.max(start - step, 0)});
  }, [disabled, start, step, onSearch]);
  const onNext = React.useCallback(e => {
    if (!disabled && start + step < count) onSearch({start: start + step});
  }, [disabled, start, step, count, onSearch]);
  const onEnd = React.useCallback(e => {
    if (!disabled && start + step < count) onSearch({start: (pages - 1) * step});
  }, [disabled, pages, start, step, count, onSearch]);
  const onChange = React.useCallback(e => {
    if (!disabled) onSearch({start: parseInt(e.target.value)});
  }, [disabled, onSearch]);

  if (!count) return null;
  return (
    <div className="d2p-search-nav">
      <span className="d2p-nav-tip">
        {_L`Showing ${start + 1} to ${start + length} of ${count}`}
      </span>
      <span className="d2p-nav-buttons">
        <span className={disabled || start <= 0 ? "d2p-disabled" : null} onClick={onHome}>&lt;&lt;</span>
        <span className={disabled || start <= 0 ? "d2p-disabled" : null} onClick={onPrev}>&lt;</span>
        <select disabled={disabled || pages <= 1} value={Math.round(start / step) * step} onChange={onChange}>
          {[...Array(pages)].map((_, pos) => <option key={pos} value={pos * step}>{pos + 1}</option>)}
        </select>
        <span className={disabled || start + length >= count ? "d2p-disabled" : null} onClick={onNext}>&gt;</span>
        <span className={disabled || start + length >= count ? "d2p-disabled" : null} onClick={onEnd}>&gt;</span>
      </span>
    </div>
  );
}

export const ProfileLink = React.forwardRef(function ProfileLink({ id, onClick, children, ...props }, ref) {
  const url = new URL(getProfilePath(id), window.location);
  const clickHandler = React.useCallback(e => {
    if (e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onClick(id);
  }, [id, onClick]);
  return <a href={url} onClick={clickHandler} {...props} ref={ref}>{children}</a>;
});

function ResultLine({ hero, onClick }) {
  return (
    <ProfileLink id={hero.id} onClick={onClick}>
      <span className={classNames(`d2p-class-icon d2p-class-${hero.class}`, hero.gender && `d2p-${hero.gender}`)}>&nbsp;</span>
      <span className={classNames("d2p-profile-name", {"d2p-unnamed" : !hero.value})}>
        {hero.value || _L`Unnamed profile`}
        {!!(hero.author && hero.value) && <span className="d2p-profile-author">{_L` by ${hero.author}`}</span>}
      </span>
      <span className="d2p-profile-saved">
        <span>{new Date(1000 * hero.date).toLocaleString()}</span>
      </span>
    </ProfileLink>
  );
}

export default function SearchResults({ results, onClick, onSearch, disabled }) {
  if (!results.count && !results.results.length) return null;
  return (
    <div>
      <div className="d2p-search-results">
        {results.results.map(hero => <ResultLine key={hero.id} hero={hero} onClick={onClick}/>)}
      </div>
      <SearchNav results={results} onSearch={onSearch} disabled={disabled}/>
    </div>
  );
}
